import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, ToggleButton, ToggleButtonGroup
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DATES = ['10 Feb', '11 Feb', '12 Feb', '13 Feb']

const DEMO_ROWS = [
	{
		office: 'Madrid Airport T4',
		cells: [
			{ name: 'FleetMax', price: '€38.50', type: 'cheaper', diff: 12.3 },
			{ name: 'DrivePro', price: '€45.20', type: 'more expensive', diff: 8.1 },
			{ name: 'FleetMax', price: '€36.80', type: 'cheaper', diff: 15.7 },
			{ name: 'CarFirst', price: '€42.90', type: 'equal', diff: null },
		],
	},
	{
		office: 'Barcelona El Prat',
		cells: [
			{ name: 'DrivePro', price: '€52.10', type: 'more expensive', diff: 6.4 },
			{ name: 'FleetMax', price: '€44.30', type: 'cheaper', diff: 9.8 },
			{ name: 'AutoRent', price: '€49.70', type: 'more expensive', diff: 3.2 },
			{ name: 'FleetMax', price: '€41.50', type: 'cheaper', diff: 11.5 },
		],
	},
	{
		office: 'Malaga Costa del Sol',
		cells: [
			{ name: 'CarFirst', price: '€35.90', type: 'cheaper', diff: 18.2 },
			{ name: 'SpeedCar', price: '€48.60', type: 'more expensive', diff: 5.3 },
			{ name: 'CarFirst', price: '€37.10', type: 'cheaper', diff: 14.9 },
			{ name: 'DrivePro', price: '€46.80', type: 'equal', diff: null },
		],
	},
	{
		office: 'Valencia Center',
		cells: [
			{ name: 'AutoRent', price: '€41.20', type: 'more expensive', diff: 4.7 },
			{ name: 'CarFirst', price: '€39.80', type: 'cheaper', diff: 7.6 },
			{ name: 'SpeedCar', price: '€50.30', type: 'more expensive', diff: 10.1 },
			{ name: 'AutoRent', price: '€43.60', type: 'cheaper', diff: 5.9 },
		],
	},
	{
		office: 'Palma de Mallorca',
		cells: [
			{ name: 'SpeedCar', price: '€47.80', type: 'equal', diff: null },
			{ name: 'FleetMax', price: '€36.50', type: 'cheaper', diff: 20.1 },
			{ name: 'DrivePro', price: '€53.20', type: 'more expensive', diff: 7.8 },
			{ name: 'CarFirst', price: '€39.90', type: 'cheaper', diff: 13.4 },
		],
	},
]

const COLOR_MAP = {
	'cheaper': 'success.light',
	'more expensive': 'error.light',
	'equal': 'secondary.light',
}

const BG_MAP = {
	'cheaper': 'rgba(46,125,50,0.08)',
	'more expensive': 'rgba(211,47,47,0.08)',
	'equal': 'transparent',
}

const ICON_MAP = {
	'cheaper': { icon: 'arrow-up', color: 'success.dark' },
	'more expensive': { icon: 'arrow-down', color: 'error.main' },
	'equal': { icon: 'equals', color: 'secondary.light' },
}


function PriceCell({ data }) {
	return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
		<Box sx={{
			display: 'flex', alignItems: 'center', justifyContent: 'space-around',
			borderRadius: '5px', border: 2, width: 170, minHeight: 40,
			borderColor: COLOR_MAP[data.type], backgroundColor: BG_MAP[data.type],
		}}>
			<Grid container spacing={1}>
				<Grid item xs={data.diff ? 7 : 12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Typography variant="subtitle1">{data.price}</Typography>
				</Grid>
				{data.diff && <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
					<IconSeeker {...ICON_MAP[data.type]} sx={{ mt: 0.2, fontSize: 12 }} />
					<Typography variant="body2">{data.diff}%</Typography>
				</Grid>}
				{!data.diff && data.type === 'equal' && <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: -0.5 }}>
					<IconSeeker icon="equals" sx={{ fontSize: 10, color: 'secondary.light' }} />
				</Grid>}
			</Grid>
		</Box>
	</Box>
}


export default function LeadingDemoData() {
	return <Grid container spacing={2}>
		{/* Table */}
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Typography variant="subtitle1" fontWeight={600}>Leading Price Table</Typography>
						<ToggleButtonGroup size="small" value="avg" exclusive>
							<ToggleButton value="min" sx={{ px: 2.5, py: 1, textTransform: 'none', fontSize: '0.72rem' }}>Min. Price</ToggleButton>
							<ToggleButton value="avg" sx={{ px: 2.5, py: 1, textTransform: 'none', fontSize: '0.72rem' }}>Avg. Price</ToggleButton>
							<ToggleButton value="max" sx={{ px: 2.5, py: 1, textTransform: 'none', fontSize: '0.72rem' }}>Max. Price</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' } }}>
									<TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'primary.main', minWidth: 160 }}>Office</TableCell>
									{DATES.map(d => <TableCell key={d}>{d}</TableCell>)}
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_ROWS.map((row, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
										<TableCell sx={{
											position: 'sticky', left: 0, zIndex: 1, fontWeight: 700,
											fontSize: '0.8rem', bgcolor: 'primary.main', color: 'white',
											borderBottom: 1, borderColor: 'primary.dark', minWidth: 160,
										}}>
											{row.office}
										</TableCell>
										{row.cells.map((cell, ci) => (
											<TableCell key={ci} sx={{ textAlign: 'center', p: 1 }}>
												<PriceCell data={cell} />
												<Typography sx={{ fontSize: '0.58rem', color: 'secondary.light', mt: 0.3 }}>
													vs {cell.name}
												</Typography>
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
